import * as z from "zod";

import type { RestSchema } from "@mountaya/client";
import { LONGITUDE_LIMIT } from "@mountaya/elements/geo";

import type { EmbedSafetyRules } from "../../embed/rules/safetyRules.js";
import { restActivitySlugSchema } from "../../itinerary/rules/activity.js";
import type { EmbeddedStudioDefaultProps } from "../../rules/types.js";
import { hexColorSchema } from "../../segments/rules/colors.js";
import { TIME_OFFSET_MAX_MINUTES, TIME_OFFSET_MIN_MINUTES } from "./timeWindow.js";

/**
 * languages is every language the contract's `Language` declares, each keyed by itself, in the
 * contract's order, which is the order a picker lists them in.
 *
 * The `satisfies` clause ties it to the generated type in both directions: a language the
 * contract adds leaves a missing key, and a key the contract does not declare is an excess
 * property of the literal, so `tsc` fails either way. Each value is typed by its own key, so
 * none can name another language in place of its own. Every preference vocabulary below is
 * declared the same way, for the same reason.
 */
const languages = {
  en: "en",
  fr: "fr",
  es: "es",
  it: "it",
  de: "de",
  zh: "zh",
  ja: "ja",
} as const satisfies { [K in RestSchema.Language]: K };

/**
 * languageSchema is the contract's `Language` vocabulary (the embed's preferred_language
 * override): every member of {@link languages}, so an out-of-range URL param is dropped rather
 * than carried as a free string.
 */
export const languageSchema = z.enum(languages);

/** themes is every theme the contract's `Theme` declares, tied as {@link languages} is. */
const themes = {
  auto: "auto",
  light: "light",
  dark: "dark",
} as const satisfies { [K in RestSchema.Theme]: K };

/**
 * themeSchema is the contract's `Theme` vocabulary: every member of {@link themes}. It is the
 * organization's stored default_props.preferred_theme vocabulary, the in-app studio concept
 * where "auto" follows the system preference. The embed narrows this to embedThemeSchema below.
 */
export const themeSchema = z.enum(themes);

/**
 * terrainVariants is every tint the contract's `TerrainVariant` declares, tied as
 * {@link languages} is.
 */
const terrainVariants = {
  light: "light",
  dark: "dark",
} as const satisfies { [K in RestSchema.TerrainVariant]: K };

/**
 * embedThemeSchema is the embed-only theme vocabulary: light or dark, never "auto".
 * An embed renders inside the publisher's page and must match that page's scheme (a
 * deliberate light or dark choice) rather than follow the visitor's OS preference, which
 * "auto" tracks, which the publisher cannot preview and which can clash with their
 * page. resolveEmbedConfig clamps an org default of "auto" to light, so an embed never
 * resolves "auto".
 *
 * Its members are the tints a base map has, so it reads {@link terrainVariants}: the theme an
 * embed takes and the tint its map takes are one vocabulary, stated once.
 */
export const embedThemeSchema = z.enum(terrainVariants);

/**
 * EmbedTheme is the resolved embed theme (light | dark), derived from embedThemeSchema.
 */
export type EmbedTheme = z.infer<typeof embedThemeSchema>;

/**
 * unitLengths is every measurement system the contract's `UnitLength` declares, tied as
 * {@link languages} is.
 *
 * `@mountaya/elements/units` lists the same members for its formatters, which read no wire
 * type. Each unit vocabulary is declared here against the contract, and `params.test.ts` holds
 * each one equal to the formatters' list.
 */
const unitLengths = {
  metric: "metric",
  imperial: "imperial",
} as const satisfies { [K in RestSchema.UnitLength]: K };

/**
 * unitLengthSchema is the contract's `UnitLength` vocabulary (the embed's
 * preferred_unit_length override): every member of {@link unitLengths}.
 */
export const unitLengthSchema = z.enum(unitLengths);

/**
 * unitPaces is every form the contract's `UnitPace` declares, tied as {@link languages} is.
 */
const unitPaces = {
  "per-time": "per-time",
  "per-distance": "per-distance",
} as const satisfies { [K in RestSchema.UnitPace]: K };

/**
 * unitPaceSchema is the contract's `UnitPace` vocabulary (the embed's
 * preferred_unit_pace override): every member of {@link unitPaces}. It chooses the FORM a
 * travel rate is stated in; the system still comes from preferred_unit_length.
 */
export const unitPaceSchema = z.enum(unitPaces);

/**
 * unitPaceSearchParam reads the embed's `?preferred_unit_pace=`, in the contract's kebab-case
 * spelling (`per-distance`) and in the snake-case one (`per_distance`).
 *
 * The snake-case spelling is read because an embed URL lives in the page a customer pasted it
 * into rather than in any store a migration reaches, and a reader that refused it would drop
 * the preference it states to the organization's default without a word. Only the reading is
 * lenient: every URL this package writes carries the kebab-case spelling, and anything that is
 * neither still falls through to the default.
 */
const unitPaceSearchParam = z
  .preprocess(
    (value) => (typeof value === "string" ? value.replaceAll("_", "-") : value),
    unitPaceSchema,
  )
  .optional()
  .catch(undefined);

/**
 * unitSlopes is every statement of a gradient the contract's `UnitSlope` declares, tied as
 * {@link languages} is.
 */
const unitSlopes = {
  degrees: "degrees",
  percentage: "percentage",
} as const satisfies { [K in RestSchema.UnitSlope]: K };

/**
 * unitSlopeSchema is the contract's `UnitSlope` vocabulary (the embed's
 * preferred_unit_slope override): every member of {@link unitSlopes}.
 */
export const unitSlopeSchema = z.enum(unitSlopes);

/**
 * unitTemperatures is every scale the contract's `UnitTemperature` declares, tied as
 * {@link languages} is.
 */
const unitTemperatures = {
  celsius: "celsius",
  fahrenheit: "fahrenheit",
} as const satisfies { [K in RestSchema.UnitTemperature]: K };

/**
 * unitTemperatureSchema is the contract's `UnitTemperature` vocabulary (the embed's
 * preferred_unit_temperature override): every member of {@link unitTemperatures}.
 */
export const unitTemperatureSchema = z.enum(unitTemperatures);

/**
 * terrainVariantSchema is the contract's `TerrainVariant` vocabulary (the embed's base map
 * light or dark tint): every member of {@link terrainVariants}. Distinct from the terrain
 * SLUG, which is a free string.
 */
export const terrainVariantSchema = z.enum(terrainVariants);

/**
 * TerrainVariant is the studio base-layer light/dark tint, derived from
 * terrainVariantSchema. Distinct from the terrain slug, which is a free string.
 */
export type TerrainVariant = z.infer<typeof terrainVariantSchema>;

/**
 * segmentsColorSchema is the embed's single-segment-color vocabulary: a hex color,
 * or the empty string meaning "no tint, every segment keeps its own color". The empty
 * member is not slack: it is the value that turns the tint OFF, both as the org's
 * stored default and as an explicit per-embed URL override, so it has to survive the
 * parse rather than be caught away. It reuses hexColorSchema, the same rule the API
 * persists segment colors under, so a tint and a segment color can never diverge on
 * what a valid color is.
 */
export const segmentsColorSchema = z.union([hexColorSchema, z.literal("")]);

/**
 * DEFAULT_TERRAIN is the fallback terrain slug when neither the URL, the org
 * default_props, nor the UI store specify one. It seeds the studio UI store and
 * backstops resolveEmbedConfig's terrain precedence.
 */
export const DEFAULT_TERRAIN = "topo";

/**
 * optionalString accepts a present, non-empty search-param string and otherwise
 * resolves to undefined, so an empty `?activity=` reads identically to an
 * absent param. Shared by the map-state content params.
 */
const optionalString = z.string().min(1).optional().catch(undefined);

/**
 * legacyActivitySlugs maps each activity slug written with its hyphens dropped
 * (`hikingandtrail`) to the kebab-case slug the contract names.
 *
 * Derived from the slugs rather than listed beside them: the run-together spelling is as
 * mechanical as the kebab-case one, so a slug with no hyphen maps to itself and nothing here
 * goes stale.
 */
const legacyActivitySlugs: ReadonlyMap<string, string> = new Map(
  restActivitySlugSchema.options.map((slug) => [slug.replaceAll("-", ""), slug]),
);

/**
 * activitySearchParam reads `?activity=`, in the contract's spelling (`hiking-and-trail`) and
 * run together (`hikingandtrail`), which it rewrites to the kebab-case slug.
 *
 * A shared search URL lives in customers' pages and messages rather than in any store a
 * migration reaches, so a reader that took only the kebab-case spelling would open such a link
 * without its activity. A value that is neither passes through as written.
 */
const activitySearchParam = optionalString.transform((value) =>
  value === undefined ? undefined : (legacyActivitySlugs.get(value) ?? value),
);

/**
 * optionalStringArray accepts the multi-value form (`?with_overlays=a&with_overlays=b`)
 * for the embed's overlay/terrain allowlists. A single value still parses to a
 * one-element array; an absent param resolves to undefined (so the org default
 * wins in resolveEmbedConfig).
 *
 * An empty entry names no slug and is left out, and a param whose every entry is empty
 * (`?with_terrains=`) reads as absent, as an empty string param does: kept, it would be an
 * allowlist of one slug that permits nothing. A list handed over already empty stays empty,
 * which states no restriction.
 */
const optionalStringArray = z
  .union([z.string(), z.array(z.string())])
  .transform((value) => {
    const entries = Array.isArray(value) ? value : [value];
    const named = entries.filter((entry) => entry !== "");

    return named.length === 0 && entries.length > 0 ? undefined : named;
  })
  .optional()
  .catch(undefined);

/**
 * numericText reads a number written as text. A value that trims to nothing is refused and
 * never read as zero, which is what `Number("")` answers: an empty `?zoom=` is an absent
 * param, as an empty string param is.
 */
const numericText = z
  .string()
  .trim()
  .min(1)
  .transform((value) => Number(value));

/**
 * numericParam is the input every numeric search param takes: the textual form a fresh URL
 * carries ({@link numericText}) or a real number, so a parsed value round-trips through the
 * search updater without re-stringifying.
 */
const numericParam = z.union([z.number(), numericText]);

/**
 * urlBoolean parses a URL boolean into a tri-state: true, false, or undefined
 * (param absent / unrecognized). undefined is the signal that the default should
 * win: only an explicit true/false overrides it. The input is a union of the
 * textual `"true"`/`"false"` form a fresh URL carries (case-insensitive) and a
 * real boolean, so the value round-trips: the authenticated studio writes the
 * parsed boolean straight back through the search updater without re-stringifying.
 */
export const urlBoolean = z
  .union([
    z.boolean(),
    z
      .string()
      .transform((value) => value.toLowerCase())
      .pipe(z.enum(["true", "false"]))
      .transform((value) => value === "true"),
  ])
  .optional()
  .catch(undefined);

/**
 * timeOffset parses the overlay time as an integer count of minutes from the
 * viewer's current clock (negative = the past), clamped to the slider window
 * [{@link TIME_OFFSET_MIN_MINUTES}, {@link TIME_OFFSET_MAX_MINUTES}], to the nearest edge and
 * never dropped, since the time-aware overlays carry tiles inside it alone. An absent or
 * unparseable value resolves to undefined, which the studio reads as "now".
 *
 * The URL encodes the time RELATIVELY (minutes-from-now) on purpose: a shared
 * link to "+6 days" must mean +6 days from whoever opens it, not a frozen instant
 * that drifts out of the forecast window as time passes. The store still holds the
 * absolute ISO instant it renders (overlayTimestamp); only the URL is relative,
 * and the studio converts between the two at the URL boundary.
 */
const timeOffset = numericParam
  .pipe(z.number().int())
  .optional()
  .catch(undefined)
  .transform((value) =>
    value === undefined
      ? undefined
      : Math.min(Math.max(value, TIME_OFFSET_MIN_MINUTES), TIME_OFFSET_MAX_MINUTES),
  );

/**
 * CAMERA_ZOOM_MIN and CAMERA_ZOOM_MAX bound the publisher's default-view zoom to a
 * sane web-map range, so a malformed or extreme `?zoom=` clamps to the nearest edge
 * rather than slamming the camera to an unusable level. The org's own safety zoom
 * range narrows this further at apply time in the embedded studio; this is only the
 * URL-parse guard.
 */
const CAMERA_ZOOM_MIN = 0;

/**
 * CAMERA_ZOOM_MAX is the closest zoom a camera is applied at. The default view's `?zoom=` clamps
 * to it, and the map contract's camera verbs refuse a zoom past it.
 */
export const CAMERA_ZOOM_MAX = 24;

/**
 * CAMERA_BEARING_MAX and CAMERA_PITCH_MAX bound the default-view orientation: the
 * bearing wraps into [0, 360) and the pitch clamps to [0, 85] (MapLibre's maximum
 * tilt), matching the renderer's own limits so a captured or hand-set angle is
 * always one the map can adopt.
 */
const CAMERA_BEARING_MAX = 360;

/**
 * CAMERA_PITCH_MAX is the steepest tilt a renderer holds, in degrees from straight down:
 * MapLibre's. The default view's `?pitch=` clamps to it, and the map contract's camera verbs and
 * a video shot refuse a pitch past it.
 */
export const CAMERA_PITCH_MAX = 85;

/**
 * boundedNumber builds a parser for an optional numeric search param clamped into
 * [min, max]. It mirrors the timeOffset shape (a number-or-numeric-string union, an
 * absent/unparseable value resolving to undefined) so the three default-view camera
 * params (zoom, bearing, pitch) share one well-tested clamp rather than restating
 * it. The transform runs after the union/pipe so only a real number is clamped.
 */
const boundedNumber = (min: number, max: number) =>
  numericParam
    .pipe(z.number())
    .optional()
    .catch(undefined)
    .transform((value) => (value === undefined ? undefined : Math.min(Math.max(value, min), max)));

/**
 * cameraZoom parses the publisher's default-view zoom, clamped to
 * [CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX]. Absent → undefined (the embed keeps the plain
 * fit-to-itinerary, with no zoom override).
 */
const cameraZoom = boundedNumber(CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX);

/**
 * cameraBearing parses the publisher's default-view bearing in degrees, wrapped into
 * [0, CAMERA_BEARING_MAX). A captured 360 reads as 0 (north); an absent param leaves
 * the embed north-up. The wrap (modulo) keeps a value the renderer accepts and a
 * full turn collapses to north rather than clamping to 359.
 */
const cameraBearing = numericParam
  .pipe(z.number())
  .optional()
  .catch(undefined)
  .transform((value) =>
    value === undefined
      ? undefined
      : ((value % CAMERA_BEARING_MAX) + CAMERA_BEARING_MAX) % CAMERA_BEARING_MAX,
  );

/**
 * cameraPitch parses the publisher's default-view pitch in degrees, clamped to
 * [0, CAMERA_PITCH_MAX] (MapLibre's tilt ceiling). Absent → undefined (the embed
 * opens flat).
 */
const cameraPitch = boundedNumber(0, CAMERA_PITCH_MAX);

/**
 * CAMERA_CENTER_LAT_MAX bounds the latitude of the default-view center to the Web Mercator
 * limit [-85, 85]: beyond it the projection, and the renderer's center, is undefined. The
 * longitude is bounded to the antimeridian, `LONGITUDE_LIMIT` of `@mountaya/elements/geo`. A
 * captured center always lands inside both, so a coordinate that exceeds either axis is a
 * malformed param and is dropped.
 */
const CAMERA_CENTER_LAT_MAX = 85;

/**
 * cameraCenterTuple is the validated [lng, lat] both input forms normalize to: a
 * finite longitude in [-180, 180] and latitude in [-85, 85] (the WGS84 + Web Mercator
 * limits). A value outside either axis fails the pipe, so cameraCenter's `.catch`
 * drops it.
 */
const cameraCenterTuple = z.tuple([
  z.number().finite().min(-LONGITUDE_LIMIT).max(LONGITUDE_LIMIT),
  z.number().finite().min(-CAMERA_CENTER_LAT_MAX).max(CAMERA_CENTER_LAT_MAX),
]);

/**
 * cameraCenter parses the publisher's default-view center into a [lng, lat] tuple. The
 * center is the tile coordinate the publisher framed, so the embed opens on exactly
 * that anchor rather than re-centering on the fit-to-itinerary. It accepts a union of the
 * textual `"lng,lat"` form a fresh URL carries (split on the comma into two finite
 * numbers) and the already-parsed tuple, so the value ROUND-TRIPS: the authenticated
 * studio writes the parsed tuple straight back through the search updater without
 * re-stringifying, the same union shape the numeric camera params and urlBoolean use.
 * Either form is range-validated (lng ∈ [-180, 180], lat ∈ [-85, 85]); a missing comma,
 * a non-numeric half, an empty half (`?center=,` is no centre, never the null island) or an
 * out-of-range value resolves to undefined (`.catch`), so a malformed param is dropped and
 * the embed falls back to the plain fit. It is paired with the zoom/bearing/pitch params:
 * together they are the full camera anchor.
 */
const cameraCenter = z
  .union([
    z
      .string()
      .transform((value) => value.split(","))
      .pipe(z.tuple([numericText, numericText])),
    cameraCenterTuple,
  ])
  .pipe(cameraCenterTuple)
  .optional()
  .catch(undefined);

/**
 * mapStateShape is the raw object shape of the shared map-state facet. It is
 * shared (not merged) so the standalone studioMapStateSearchSchema, the merged
 * embedSearchSchema, and the authenticated studio page all build from one
 * source rather than restating the content/display params. The page extends it
 * with the agent-thread params.
 */
export const mapStateShape = {
  activity: activitySearchParam,
  bearing: cameraBearing,
  center: cameraCenter,
  collection_id: optionalString,
  enrichments: optionalStringArray,
  itinerary_id: optionalString,
  overlay: optionalString,
  pitch: cameraPitch,
  terrain: optionalString,
  time_offset: timeOffset,
  zoom: cameraZoom,
} as const;

/**
 * MAP_STATE_SEARCH_KEYS names the search params that mirror the VISIBLE map
 * state (the overlay, its stacked enrichments, the base terrain, and the
 * overlay time offset), which the authenticated studio live-writes back into the
 * URL as the user toggles them. It is the single source of truth shared by the
 * URL-sync hook (which mirrors exactly these keys) and the unsaved-edits blocker
 * (which exempts a same-itinerary navigation that changes only these keys, so
 * toggling an overlay mid-edit does not raise the leave-an-edit prompt). The
 * content-selection keys (itinerary_id and the rest of mapStateShape)
 * are deliberately absent: changing those is a real content navigation the
 * blocker must still gate.
 */
export const MAP_STATE_SEARCH_KEYS = ["overlay", "enrichments", "terrain", "time_offset"] as const;

/**
 * MapStateSearchKey is one live-written map-state search param, derived from
 * MAP_STATE_SEARCH_KEYS.
 */
export type MapStateSearchKey = (typeof MAP_STATE_SEARCH_KEYS)[number];

/**
 * AGENT_PANEL_SEARCH_KEYS names the co-pilot panel's URL keys (the ordered
 * thread set, `threads`, and the active tab, `activeThread`), which
 * AgentThreadsProvider mirrors into the itinerary search as the user opens, closes,
 * or switches an AI conversation, so a reload reconnects every in-flight chat.
 * Like the map-state keys, toggling the co-pilot REFINES the current view rather
 * than leaving it: opening the assistant enhances the edit in progress, it does
 * not navigate away from it. The unsaved-edits blocker unions these with
 * MAP_STATE_SEARCH_KEYS so a same-itinerary navigation whose only changed keys are
 * the panel keys never raises the leave-an-edit prompt.
 */
export const AGENT_PANEL_SEARCH_KEYS = ["activeThread", "threads"] as const;

/**
 * studioMapStateSearchSchema is the SHARED studio URL-state facet: the
 * content/display params the authenticated webapp live-writes and the embed reads
 * as static config. itinerary_id is the single content selector.
 */
export const studioMapStateSearchSchema = z.object(mapStateShape);

/**
 * StudioMapStateSearch is the parsed shared map-state facet, derived from
 * studioMapStateSearchSchema.
 */
export type StudioMapStateSearch = z.infer<typeof studioMapStateSearchSchema>;

/**
 * embedConfigSearchSchema is the embed-only facet: the preference overrides, the
 * terrain variant, the boolean with_* control flags (textual "true"/"false"),
 * and the with_overlays/with_terrains allowlist arrays. Every field is optional
 * here; resolveEmbedConfig applies the URL > default_props > fallback precedence.
 * `embedded` is always true, so it does not appear here.
 */
export const embedConfigSearchSchema = z.object({
  preferred_language: languageSchema.optional().catch(undefined),
  preferred_theme: embedThemeSchema.optional().catch(undefined),
  preferred_unit_length: unitLengthSchema.optional().catch(undefined),
  preferred_unit_pace: unitPaceSearchParam,
  preferred_unit_slope: unitSlopeSchema.optional().catch(undefined),
  preferred_unit_temperature: unitTemperatureSchema.optional().catch(undefined),
  // An EMPTY value is meaningful and distinct from an absent one: `segments_color=`
  // turns the tint off for this embed even when the org sets one, while omitting the
  // param falls through to the org default. Only a malformed value catches to
  // undefined.
  segments_color: segmentsColorSchema.optional().catch(undefined),
  terrain_variant: terrainVariantSchema.optional().catch(undefined),
  with_directions_control: urlBoolean,
  with_download_control: urlBoolean,
  with_navigation_control: urlBoolean,
  with_overlays: optionalStringArray,
  with_overlays_control: urlBoolean,
  with_preferences_control: urlBoolean,
  with_profile_analysis: urlBoolean,
  with_profile_characteristics: urlBoolean,
  with_profile_elevation: urlBoolean,
  with_profile_pace_control: urlBoolean,
  with_streetview_control: urlBoolean,
  with_terrains: optionalStringArray,
});

/**
 * EmbedConfigSearch is the parsed embed-only facet, derived from
 * embedConfigSearchSchema.
 */
export type EmbedConfigSearch = z.infer<typeof embedConfigSearchSchema>;

/**
 * embedAuthSearchSchema is the credential facet: the publishable key that
 * resolves the org and the optional session token for require_session_token
 * orgs. These are passed to the API embed-auth strategy (X-API-Key /
 * X-Session-Token), never named with the reserved credential query params.
 *
 * `preview` is the owner-truthful preview signal the embed wizard sets on its OWN
 * preview iframe (never the copyable snippet). When true, the same-origin preview
 * authenticates the surface's data + tile reads with the owner's session cookie
 * instead of the publishable key, so the publisher sees their own itinerary
 * regardless of privacy, while a real cross-site embed (which never carries this
 * flag, and whose cookie would not ride cross-site anyway) stays visitor-gated. It
 * is a same-origin-only convenience: the host's CSP still gates the document.
 *
 * `organization_id` is the PREVIEW-ONLY transport that lets the preview resolve the
 * org by id over the publisher's session (the publishable-key lookup the visitor
 * path uses is unavailable when no key is selected). It rides only on the wizard's
 * own preview iframe alongside `preview`; it is never part of the resolved
 * EmbedConfig and never emitted into the copyable snippet, so a visitor's URL never
 * carries it. A visitor (no `preview`) still resolves the org from the publishable
 * key and never reads this param.
 */
export const embedAuthSearchSchema = z.object({
  organization_id: optionalString,
  preview: urlBoolean,
  publishable_key: optionalString,
  session_token: optionalString,
});

/**
 * EmbedAuthSearch is the parsed credential facet, derived from
 * embedAuthSearchSchema.
 */
export type EmbedAuthSearch = z.infer<typeof embedAuthSearchSchema>;

/**
 * embedCompatShape carries `route_id`, a permanent reading of `itinerary_id`: embeds already in
 * customers' pages pass the itinerary under that name, and a link published once is read for as
 * long as it stands. The embed schema accepts it and resolveEmbedConfig folds it into itineraryId
 * (itinerary_id wins when both are set). It is scoped to the embed schema so the authenticated
 * /studio page does not inherit it; a URL the studio writes names `itinerary_id`.
 */
const embedCompatShape = { route_id: optionalString } as const;

/**
 * embedSearchSchema is the merged /studio/embed search schema: the three facets
 * the embed page's validateSearch composes. It extends the auth + config object
 * schemas with the shared map-state shape, so the embed reads the content/display
 * params alongside the credentials and preferences in one parse. It also carries the
 * `route_id` reading (embedCompatShape).
 */
export const embedSearchSchema = embedAuthSearchSchema
  .extend(embedConfigSearchSchema.shape)
  .extend(mapStateShape)
  .extend(embedCompatShape);

/**
 * EmbedSearch is the parsed merged embed search, derived from embedSearchSchema.
 */
export type EmbedSearch = z.infer<typeof embedSearchSchema>;

/**
 * shareSearchSchema is the /studio/share search schema: the shared target (an
 * itinerary or a collection) and the resource-scoped share token that authorizes
 * the read-only view. All three reuse the shared optional-string-with-catch parser,
 * so a malformed or absent param resolves to undefined and the page degrades to
 * the invalid-link screen rather than throwing. Unlike the embed schema it carries
 * NO display-config params: the share view honors the app defaults (and the org
 * default_props), so there is nothing for a visitor to tune. The token is read out
 * of the shared link here and then rides the X-Share-Token header on every read
 * (never a URL credential the backend logs).
 *
 * EXACTLY ONE target is meaningful, because a token is minted for one resource and
 * the surface has to know which one it was handed. A link naming both drops both
 * rather than picking: guessing would render one resource with a token the other
 * was minted for, and the invalid-link screen is the honest answer.
 */
export const shareSearchSchema = z
  .object({
    collection_id: optionalString,
    itinerary_id: optionalString,
    share_token: optionalString,
  })
  .transform((search) =>
    search.collection_id !== undefined && search.itinerary_id !== undefined
      ? { ...search, collection_id: undefined, itinerary_id: undefined }
      : search,
  );

/**
 * ShareSearch is the parsed /studio/share search, derived from shareSearchSchema.
 */
export type ShareSearch = z.infer<typeof shareSearchSchema>;

/**
 * embedConfigSchema is the fully-resolved embed configuration the studio
 * components consume. It is the post-precedence shape: every control flag is a
 * concrete boolean, the preference/terrain values are resolved, and the content
 * selection carries the resolved map-state ids. Zod-first so resolution output is
 * validated and the EmbedConfig type is derived from it.
 */
export const embedConfigSchema = z.object({
  activity: z.string().nullable(),
  collectionId: z.string().nullable(),
  embedded: z.literal(true),
  enrichments: z.array(z.string()),
  itineraryId: z.string().nullable(),
  overlay: z.string().nullable(),
  preferredLanguage: languageSchema,
  preferredTheme: embedThemeSchema,
  preferredUnitLength: unitLengthSchema,
  preferredUnitPace: unitPaceSchema,
  preferredUnitSlope: unitSlopeSchema,
  preferredUnitTemperature: unitTemperatureSchema,
  // The LAST line of defence before this value reaches MapLibre: a tint is written
  // verbatim into a line-color and into a marker sprite id, and an invalid color
  // breaks the itinerary layers outright. `.catch("")` degrades a malformed org
  // value to "no tint" instead, so a bad stored color costs the tint, never the map.
  segmentsColor: segmentsColorSchema.catch(""),
  terrain: z.string(),
  terrainVariant: terrainVariantSchema,
  // Whether a collection embed draws the collection's itineraries: the organization's own
  // default, held to its safety rule, which the specification is read with. Null leaves it to the
  // default the server resolves for the credential, which is what a surface that holds no
  // organization defaults states.
  withCollectionItineraries: z.boolean().nullable(),
  // Whether a collection embed draws the collection's zones, resolved and read the same way.
  withCollectionZones: z.boolean().nullable(),
  withDirectionsControl: z.boolean(),
  withDownloadControl: z.boolean(),
  withNavigationControl: z.boolean(),
  withOverlays: z.array(z.string()),
  withOverlaysControl: z.boolean(),
  withPreferencesControl: z.boolean(),
  withProfileAnalysis: z.boolean(),
  withProfileCharacteristics: z.boolean(),
  withProfileElevation: z.boolean(),
  withProfilePaceControl: z.boolean(),
  withStreetviewControl: z.boolean(),
  withTerrains: z.array(z.string()),
});

/**
 * EmbedConfig is the fully-resolved embed configuration, derived from
 * embedConfigSchema.
 */
export type EmbedConfig = z.infer<typeof embedConfigSchema>;

/**
 * embedConfigFallbacks is what each member of an embed's configuration resolves to when neither
 * the URL nor the organization's stored defaults answer it: the baseline a new organization
 * carries. Navigation and preferences on, the elevation and characteristics profile sections on
 * with analysis off, street view, directions, download and the overlay time slider off, no
 * allowlist, the light topographic base map, no segment tint, and a collection's itineraries and
 * zones drawn.
 *
 * `embed/rules/defaults.ts` states the same baseline for an organization with no stored
 * defaults at all, and `params.test.ts` holds the two equal member by member.
 */
const embedConfigFallbacks = {
  preferred_language: "en",
  preferred_unit_length: "metric",
  preferred_unit_pace: "per-time",
  preferred_unit_slope: "degrees",
  preferred_unit_temperature: "celsius",
  terrain_variant: "light",
  with_collection_itineraries: true,
  with_collection_zones: true,
  with_directions_control: false,
  with_download_control: false,
  with_navigation_control: true,
  with_overlays_control: false,
  with_preferences_control: true,
  with_profile_analysis: false,
  with_profile_characteristics: true,
  with_profile_elevation: true,
  with_profile_pace_control: true,
  with_streetview_control: false,
} as const satisfies Partial<EmbeddedStudioDefaultProps>;

/** slugListSchema is a list of slugs, the shape an allowlist is stored in. */
const slugListSchema = z.array(z.string());

/**
 * storedOr reads one stored default through its schema, and answers `fallback` for a value the
 * schema does not hold.
 *
 * An organization's stored defaults are typed by the contract this build was compiled against,
 * and the record itself may be older or newer than the build: a language the contract gained
 * since, a member an older record lacks. Each member is read by itself, so one such value costs
 * that member its default and never the embed.
 */
const storedOr = <T>(schema: z.ZodType<T>, stored: unknown, fallback: T): T => {
  const parsed = schema.safeParse(stored);

  return parsed.success ? parsed.data : fallback;
};

/**
 * resolveBoolean applies the control-flag precedence: an explicit URL value (true or false)
 * wins, else the organization's stored value, else the fallback. undefined from the URL means
 * "not specified": the urlBoolean parser maps an unrecognized or absent param to undefined. A
 * stored value that is no boolean reads as absent.
 */
const resolveBoolean = (
  urlValue: boolean | undefined,
  orgDefault: unknown,
  fallback: boolean,
): boolean => urlValue ?? (typeof orgDefault === "boolean" ? orgDefault : fallback);

/**
 * resolveEmbedConfig folds the parsed URL search params over the org's
 * default_props into the fully-resolved EmbedConfig the studio consumes. The
 * precedence is URL value > organization default_props > the platform's baseline
 * ({@link embedConfigFallbacks}). It is render-free and pure (the embed page calls it
 * in its loader); content ids come from the shared map-state facet, control flags
 * and preferences from the embed-config facet + default_props.
 *
 * A stored default this build cannot read falls back for that member alone: a value outside
 * the vocabulary this build knows (a language the contract gained since), a member of another
 * type, or one the record lacks resolves to the baseline, and every other member resolves as
 * stored. So the function answers a configuration for any stored record, and never throws over
 * one.
 *
 * `collection_id` and `itinerary_id` are both CONTENT SELECTORS, and which of them
 * is present decides what the embed renders: a collection alone renders the whole
 * collection, a collection with an itinerary renders the collection framed on that
 * itinerary, and an itinerary alone renders just that itinerary. What a collection
 * embed may expose of its contents is clamped server-side by the owning
 * organization's settings, so there is nothing for a publisher to spell per embed.
 * `withCollectionItineraries` and `withCollectionZones` carry what that organization lets a
 * collection embed draw of the collection's itineraries and zones: its own default
 * (`default_props.with_collection_itineraries`, `default_props.with_collection_zones`), held to its
 * safety rule (`safety_rules.with_collection_itineraries`, `safety_rules.with_collection_zones`)
 * where both are stated, so false when either is. The embed reads the collection's specification
 * with both, so a preview read under the publisher's session declares no itinerary or zone layer
 * a visitor's key would be refused, frames on the extent that key is served, and draws what that
 * key is served: the collection's published public itineraries and zones.
 *
 * @param search - The parsed embed search params.
 * @param orgDefaultProps - The organization's stored embed defaults, as its record holds them.
 * @param safetyRules - The organization's `settings.safety_rules`, or undefined when it states
 *   none, in which case no rule holds a default back.
 */
export const resolveEmbedConfig = (
  search: EmbedSearch,
  orgDefaultProps: EmbeddedStudioDefaultProps,
  safetyRules?: EmbedSafetyRules | undefined,
): EmbedConfig => {
  const fallbacks = embedConfigFallbacks;
  // `route_id` is the reading of an older embed's itinerary; itinerary_id wins when both are set.
  const itineraryId = search.itinerary_id ?? search.route_id ?? null;

  // The parse validates what was resolved, and its one `.catch` degrades a malformed stored
  // tint to none. Every other stored member is narrowed above it, so nothing here throws.
  return embedConfigSchema.parse({
    activity: search.activity ?? null,
    collectionId: search.collection_id ?? null,
    embedded: true,
    // The active enrichment overlays (e.g. hillshade), from the repeated `enrichments`
    // param; empty when absent (no org default, like `overlay`). The programmatic `pois`
    // enrichment is never carried on the URL, so it never seeds here.
    enrichments: search.enrichments ?? [],
    itineraryId,
    overlay: search.overlay ?? null,
    preferredLanguage:
      search.preferred_language ??
      storedOr(languageSchema, orgDefaultProps.preferred_language, fallbacks.preferred_language),
    // The embed has no "auto" theme, so an org default of "auto" (valid for the
    // in-app studio + stored default_props) clamps to light here; an explicit URL
    // light/dark override still wins.
    preferredTheme:
      search.preferred_theme ?? (orgDefaultProps.preferred_theme === "dark" ? "dark" : "light"),
    preferredUnitLength:
      search.preferred_unit_length ??
      storedOr(
        unitLengthSchema,
        orgDefaultProps.preferred_unit_length,
        fallbacks.preferred_unit_length,
      ),
    preferredUnitPace:
      search.preferred_unit_pace ??
      storedOr(unitPaceSchema, orgDefaultProps.preferred_unit_pace, fallbacks.preferred_unit_pace),
    preferredUnitSlope:
      search.preferred_unit_slope ??
      storedOr(
        unitSlopeSchema,
        orgDefaultProps.preferred_unit_slope,
        fallbacks.preferred_unit_slope,
      ),
    preferredUnitTemperature:
      search.preferred_unit_temperature ??
      storedOr(
        unitTemperatureSchema,
        orgDefaultProps.preferred_unit_temperature,
        fallbacks.preferred_unit_temperature,
      ),
    // The embed-only segment tint. Unlike terrain this uses ?? (not ||), because an
    // empty string is a MEANINGFUL value here rather than an unspecified one: it is
    // how a publisher turns an org-wide tint off for one embed. So an explicit
    // `segments_color=` beats the org default, and only an ABSENT param falls through
    // to it. The resolved "" means every segment keeps its own color.
    segmentsColor: search.segments_color ?? orgDefaultProps.segments_color ?? "",
    // Terrain uses || (not ??) so an empty default_props.terrain falls through to
    // the hardcoded fallback (an empty string must not count as "specified").
    terrain: search.terrain || storedOr(z.string(), orgDefaultProps.terrain, "") || DEFAULT_TERRAIN,
    terrainVariant:
      search.terrain_variant ??
      storedOr(terrainVariantSchema, orgDefaultProps.terrain_variant, fallbacks.terrain_variant),
    // No URL spells either: what a collection embed may draw of its contents is the
    // organization's setting, which the server clamps to its safety rule for a key. The rule is
    // applied here too, so a read under the publisher's own session, which the server does not
    // clamp, asks for no layer a visitor would be refused. A rule the record does not state holds
    // nothing back.
    withCollectionItineraries:
      storedOr(
        z.boolean(),
        orgDefaultProps.with_collection_itineraries,
        fallbacks.with_collection_itineraries,
      ) && storedOr(z.boolean(), safetyRules?.with_collection_itineraries, true),
    withCollectionZones:
      storedOr(
        z.boolean(),
        orgDefaultProps.with_collection_zones,
        fallbacks.with_collection_zones,
      ) && storedOr(z.boolean(), safetyRules?.with_collection_zones, true),
    withDirectionsControl: resolveBoolean(
      search.with_directions_control,
      orgDefaultProps.with_directions_control,
      fallbacks.with_directions_control,
    ),
    withDownloadControl: resolveBoolean(
      search.with_download_control,
      orgDefaultProps.with_download_control,
      fallbacks.with_download_control,
    ),
    withNavigationControl: resolveBoolean(
      search.with_navigation_control,
      orgDefaultProps.with_navigation_control,
      fallbacks.with_navigation_control,
    ),
    withOverlays:
      search.with_overlays ?? storedOr(slugListSchema, orgDefaultProps.with_overlays, []),
    withOverlaysControl: resolveBoolean(
      search.with_overlays_control,
      orgDefaultProps.with_overlays_control,
      fallbacks.with_overlays_control,
    ),
    withPreferencesControl: resolveBoolean(
      search.with_preferences_control,
      orgDefaultProps.with_preferences_control,
      fallbacks.with_preferences_control,
    ),
    withProfileAnalysis: resolveBoolean(
      search.with_profile_analysis,
      orgDefaultProps.with_profile_analysis,
      fallbacks.with_profile_analysis,
    ),
    withProfileCharacteristics: resolveBoolean(
      search.with_profile_characteristics,
      orgDefaultProps.with_profile_characteristics,
      fallbacks.with_profile_characteristics,
    ),
    withProfileElevation: resolveBoolean(
      search.with_profile_elevation,
      orgDefaultProps.with_profile_elevation,
      fallbacks.with_profile_elevation,
    ),
    withProfilePaceControl: resolveBoolean(
      search.with_profile_pace_control,
      orgDefaultProps.with_profile_pace_control,
      fallbacks.with_profile_pace_control,
    ),
    withStreetviewControl: resolveBoolean(
      search.with_streetview_control,
      orgDefaultProps.with_streetview_control,
      fallbacks.with_streetview_control,
    ),
    withTerrains:
      search.with_terrains ?? storedOr(slugListSchema, orgDefaultProps.with_terrains, []),
  });
};
